import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Alert, AlertTitle } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { getSGRWarrantyRequestFile } from 'lib/api/sgr';
import { showAlert } from 'lib/functions/utils';
import CustomButton from 'lib/designSystem/Button';
import { convertHtmlToPdf } from 'lib/common/HtmlToPdf';
import AlertBanner from 'lib/designSystem/AlertBanner';

interface WarrantyFileProps {
  warranty: any;
}

const WarrantyFile: React.FC<WarrantyFileProps> = ({ warranty }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isWarrantyActive = warranty.status === 'ACTIVE';

  const loadPdf = async () => {
    if (!isWarrantyActive) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const htmlContent = await getSGRWarrantyRequestFile(
        dispatch,
        warranty.id,
      );

      const pdfBlob = await convertHtmlToPdf(htmlContent);
      const url = URL.createObjectURL(pdfBlob);
      const viewerUrl = `${url}#pagemode=none`;

      setViewerUrl(viewerUrl);
      setBlobUrl(url);
    } catch (error) {
      console.error(error);
      setError('Error al cargar el documento');
      showAlert(dispatch, 'error', 'Error al cargar el documento');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadPdf();
  };

  useEffect(() => {
    if (isWarrantyActive) {
      loadPdf();
    }

    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [warranty.id, isWarrantyActive]);

  if (!isWarrantyActive) {
    return (
      <AlertBanner
        severity="info"
        icon={<InfoIcon sx={{ fontSize: '1.5rem' }} />}
        title="Garantía no activa"
        message="La garantía debe estar en estado ACTIVO para poder mostrar el certificado de garantía. Este documento solo está disponible cuando la operación ha sido completamente procesada y aprobada."
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Paper
        sx={{
          flex: 1,
          boxShadow: 'none',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1,
            }}
          >
            <Typography>Cargando documento...</Typography>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Alert severity="error" sx={{ maxWidth: 400 }}>
              <AlertTitle>Error</AlertTitle>
              <Typography variant="body2">{error}</Typography>
              <Box sx={{ mt: 1 }}>
                <CustomButton variant="outlined" onClick={handleRefresh}>
                  Reintentar
                </CustomButton>
              </Box>
            </Alert>
          </Box>
        )}

        {viewerUrl && !loading && !error && (
          <iframe
            src={viewerUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              minHeight: '600px',
            }}
            title={`Documento de Garantía ${warranty.id}`}
          />
        )}
      </Paper>
    </Box>
  );
};

export default WarrantyFile;
